/* Diffuser Help (sections/ap-diffuser-help.liquid) — immersive, one screen at a time.
 * Every screen is its own URL (?track=fix|use&model=&issue=&path= / &s=), so Back,
 * reload and shared support links land on the exact screen. Two tracks: "fix" (guided
 * troubleshooting) and "use" (how-to walkthrough, data in assets/ap-dh-howto.js).
 * Facts: PDF manuals (Notion "Waterless Diffuser Features Guide"), the Notion Support
 * Playbook guided-troubleshooting tree (Sureel 2026-09-07) and
 * custom.waterless_diffuser_profile_v2. Rules carried over from the playbook:
 * one step at a time, hum check before seating, cleaner before alcohol, bottle
 * seats with NO click, use the included cable, never return / refund / warranty
 * copy, escalate with a short video. Do not add specs that aren't in those sources. */
(() => {
  const root = document.querySelector('[data-apdh]');
  if (!root) return;
  const CFG = JSON.parse(root.querySelector('[data-apdh-config]').textContent);
  const panel = root.querySelector('[data-apdh-panel]');
  const ticketEl = root.querySelector('[data-apdh-ticket]');
  const L = CFG.links;
  const HOWTO = window.APDH_HOWTO || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Below the two-column breakpoint the ticket sits above the step, so it starts
  // collapsed to a one-line summary and never pushes the step below the fold.
  const narrow = window.matchMedia('(max-width: 989px)');
  if (narrow.matches) ticketEl.querySelector('details').open = false;

  /* ------------------------------------------------------------------ models */
  // Tower family: bottle + straw under a lift-off top. `tap` (tap out air bubbles) is a
  // tower-only fix and does NOT apply to PowerFlow, which uses a different mechanism (owner 2026-10-04).
  const TOWER = {
    seat: 'tower',
    tap: true,
    // PD10/PD16/PD17 manuals: spare nozzle + metal hook in the box; remote "Restore" = factory reset.
    nozzle: true,
    reset: 'If the settings seem stuck, press the Restore button (top right on the remote) to go back to factory settings.',
    on: 'Hold the power button on the front for 2 seconds, or press ON/OFF on the remote.',
    strong: 'Press the power button once at a time to step through L1–L6, or press L1 on the remote. L1 is the strongest: 30 seconds on, 60 seconds off.',
    timer: 'Out of the box it runs for 4 hours, then switches itself off. Double-press the power button to change the timer: 1h, 2h, 4h, 8h, or all four lights off to cancel the timer.',
    pulse: 'It sprays for 30 seconds, then pauses for 1–6 minutes depending on the level.',
    cord: 'Use the USB-C cable and adapter that came in the box.',
  };
  // Three-rhythm button models (manuals ID800, DA27, WD01; profiles for Night Light, Aromar+).
  const BTN3 = {
    pulse: 'It sprays, then pauses. The pause gets shorter as you step up: 15 seconds on and 165 off, then 60 on and 120 off, then 120 on and 60 off.',
  };
  const M = {
    'tower-pro': { ...TOWER, name: 'Tower Pro', h: ['tower-pro-waterless-scent-diffuser'], kw: /tower pro/i, app: 'smartlife',
      manual: 'pd17-pd17s-tower' },
    'tower-ultra': { ...TOWER, name: 'Tower Ultra', h: ['tower-ultra-rechargeable-waterless-scent-diffuser'], kw: /tower ultra/i, app: 'smartlife', battery: true, nozzle: false, reset: null,
      on: 'Hold the power button for 2–3 seconds, or press ON/OFF on the remote.',
      timer: 'Out of the box it runs for 4 hours, then switches itself off. Double-press the power button to change the timer (1h, 2h, 4h or 8h), or check the remote or the Smart Life app for a timer. The battery also needs charging now and then.',
      cord: 'Charge it with the USB-C cable and power adapter that came in the box.' },
    'fabric-tower': { ...TOWER, name: 'Fabric Tower', h: ['fabric-tower-waterless-scent-diffuser'], kw: /fabric tower/i, manual: 'pd16-pd16s-fabric-tower' },
    'soft-illumination': { ...TOWER, name: 'Soft Illumination Tower', kw: /soft illumination|illuminating tower/i, manual: 'pd10-soft-illumination-tower',
      h: ['soft-illumination-tower-waterless-cold-air-diffuser-luxury-smart-electric-scent-machine-for-home-hotel-commercial-spaces-coverage-up-to-2000-sq-ft-coverage-120ml-capacity-remote-control-night-light-3-intensity-levels'],
      lightDefault: 'Out of the box it starts on its lightest setting (30 seconds on, 6 minutes off), so step it up to L1 for a stronger scent.' },
    powerflow: { name: 'PowerFlow Tower', h: ['powerflow-tower-waterless-scent-diffuser'], kw: /powerflow/i, seat: 'powerflow', manual: 'powerflow-tower',
      on: 'Press the power button once to turn it on, or press the power button on the remote. Hold the power button for 2 seconds to turn it off.',
      // Manual p.1 item 7: 10/105, 30/150, 60/300, 90/810. 30S and 60S both spray ~10 min per hour, the most of the four.
      strong: 'Press the power button again, or use the Mist buttons on the remote, to switch between 10S, 30S, 60S and 90S. For the strongest scent use 30S or 60S: both spray about 10 minutes every hour.',
      timer: 'It has 1, 3, 6 and 8-hour timers, set with the timer button or the remote. When the timer runs out it switches itself off. Check the 1H, 3H, 6H and 8H lights to see if a timer is set.',
      pulse: 'It sprays in bursts with pauses in between; the mode you pick sets the rhythm.',
      cord: 'Use the USB-C cable and adapter that came in the box.' },
    candlelight: { name: 'Candlelight Rechargeable', kw: /candlelight (rechargeable|tower)/i, seat: 'candle', battery: true, manual: 'id800-candlelight-rechargeable',
      h: ['candlelight-tower-waterless-cold-air-diffuser-for-home-hotel-commercial-spaces-coverage-up-to-1000-sq-ft-coverage-rechargeable-7-color-night-light-3-intensity-levels'],
      on: 'Press and hold the power button for 1–2 seconds.',
      strong: 'Click the power button to step through its three spray rhythms. The strongest is 120 seconds on, 60 seconds off.',
      lightDefault: 'Out of the box it starts on its lightest setting (15 seconds on, 165 seconds off), so click the power button twice for the strongest.',
      timer: 'It switches itself off after 8 hours by default (no timer light). The timer button cycles 1h, 2h, 3h and 8h.',
      pulse: BTN3.pulse, cord: 'Charge it only with the cable that came in the box. Other chargers can cause odd behavior.' },
    mini: { name: 'Portable Mini', h: ['mini-waterless-scent-diffuser-car-home-rechargeable'], kw: /portable (rechargeable|mini)/i, seat: 'mini', battery: true, manual: 'da27-portable-rechargeable',
      on: 'Touch the On/Off (power) button.',
      strong: 'Short-touch the On/Off button to step through 15, 60 and 120-second sprays. 120 seconds is the strongest.',
      lightDefault: 'Out of the box it starts on its lightest setting (15 seconds on, 165 seconds off), so short-touch the On/Off button twice for the strongest.',
      timer: 'It switches itself off after 8 hours by default. The clock button sets 1, 2 or 3 hours.',
      pulse: BTN3.pulse,
      cord: 'Charge it only with the USB-C cable that came in the box. The power light flickers while charging and stays solid when full (about 2–3 hours).' },
    'luxury-plugin': { name: 'Luxury Aluminum Plug-In', kw: /luxury aluminum/i, seat: 'plug', manual: 'wd01-luxury-aluminum-plugin',
      h: ['waterless-cold-air-plug-in-wall-diffuser-luxury-aluminum-scent-machine-600-1000-sq-ft-coverage-night-light-auto-shutoff-daylight-sensor-3-intensity-levels'],
      on: 'Hold the On/Off button for 2 seconds.',
      strong: 'Short-touch the On/Off button to step through 15, 60 and 120-second sprays. 120 seconds is the strongest.',
      lightDefault: 'Out of the box it starts on its lightest setting (15 seconds on, 165 seconds off), so short-touch the On/Off button twice for the strongest.',
      timer: 'It switches itself off after 8 hours by default (Timer button: 1h, 2h, 3h, 8h). If Light Sensor Mode is on, the timer doesn\'t apply: it runs while there\'s light and stops when the room gets dark. Hold Timer and On/Off together for 1 second to turn that mode off.',
      pulse: BTN3.pulse, cord: 'Plug it straight into a wall outlet.' },
    contour: { name: 'Contour Smart Plug-In', h: ['contour-smart-plug-in-waterless-scent-diffuser'], kw: /contour/i, seat: 'contour', app: 'scent', manual: 'a323-contour-smart-plugin',
      on: 'Short-press the Mode button. It cycles 3H, 6H, Smart (Bluetooth) and Off. A red light means it\'s set to Off.',
      strong: 'Short-press the Grade button to step 1, 2, 3. Grade 3 is the strongest: 15 seconds on, 60 seconds off.',
      timer: 'In 3H mode it runs for 3 hours, then rests for 21 hours (yellow light). In 6H mode it runs 6 hours, then rests 18. A yellow light means it\'s resting, not broken. In Smart mode, the schedule you set in the Scent Marketing app decides when it runs.',
      pulse: 'It sprays for 15 seconds, then pauses for 1–2 minutes depending on the grade.',
      cord: 'Plug it straight into a wall outlet.' },
    'arc-touch': { name: 'Arc Touch Plug-In', h: ['arc-touch-plug-in-waterless-scent-diffuser'], kw: /arc touch/i, seat: 'plug',
      on: 'Press and hold the timer button to turn it on.',
      strong: 'Use the touch controls to step up to the strongest of its 4 modes.',
      timer: 'It has 1, 2, 4 and 8-hour timers. Check that one isn\'t set.',
      pulse: 'It sprays in short bursts with longer pauses in between, even on the strongest setting.', cord: 'Plug it straight into a wall outlet.' },
    'night-light': { name: 'Night Light Plug-In', kw: /night light plug/i, seat: 'plug',
      h: ['light-sensor-waterless-plug-in-cold-air-diffuser-for-home-bathroom-office-spaces-coverage-up-to-1000-sq-ft-coverage-sensor-light-quiet-35db-4-timer-levels'],
      on: 'Hold the power button for 2–3 seconds.',
      strong: 'Press the mode button to step up to the strongest of its 3 settings.',
      timer: 'It has 1, 2, 3 and 8-hour timers. When the timer runs out it stops, so check that one isn\'t set.',
      pulse: BTN3.pulse, cord: 'Plug it straight into a wall outlet.' },
    'smart-car': { name: 'Smart Car', kw: /smart car/i, seat: 'car', battery: true, car: true,
      h: ['smart-car-waterless-cold-air-scent-diffuser-best-for-cars-small-spaces-3-intensity-levels-rechargeable-3-fragrance-oils-included'],
      on: 'Press the power button.',
      strong: 'Press the button to step up to the strongest of its 3 settings.',
      timer: 'It starts and stops on its own when it senses the car is running, so it will pause when the car is parked.',
      pulse: 'It releases a quick puff, then waits; the setting decides how often.',
      cord: 'Charge it with the USB-C cable that came in the box (about an hour).' },
    // Owner 2026-10-04: HVAC+ pairs with the Smart Life app.
    'hvac-plus': { name: 'HVAC+', h: ['hvac-plus-waterless-scent-diffuser'], kw: /hvac\+|hvac plus/i, seat: 'hvac', app: 'smartlife-hvac', manual: 'hvac-plus',
      on: 'Press and hold the power button in the center of the panel for 1 second.',
      strong: 'Press the MIST button to step from Level 1 to Level 3, or raise it in the app. Level 3 is the strongest: 120 seconds on, 60 seconds off. It starts on Level 1, the lightest, so step it up gradually.',
      timer: 'It switches itself off after 8 hours by default. Press TIME to choose 1H, 2H, 3H or 8H. Also check for a schedule in the app.',
      fan: 'A single press of the power button turns the fan off. If it\'s on but no scent comes out, press the power button once to turn the fan back on.',
      pulse: 'It runs in on/off cycles you can adjust on the touchscreen or in the app.',
      cord: 'Use the 12V power adapter that came in the box.' },
    'aromar-plus': { name: 'Aromar+ Plug-In', h: ['plug-in-waterless-diffuser-black'], kw: /aromar\+?\s*plug|aromar\+ plug-in/i, seat: 'plug', app: 'galaxy', hidden: true,
      on: 'Hold the power button for 2–3 seconds.',
      strong: 'Press the mode button to step up to the strongest of its 3 settings (120 seconds on, 60 off).',
      timer: 'If its light-responsive mode is on, it only runs when there\'s light in the room. Also check for a schedule in the Aroma Galaxy app.',
      pulse: BTN3.pulse, cord: 'Plug it straight into a wall outlet.' },
    tower: { name: 'Tower (original)', h: ['tower-waterless-scent-diffuser'], kw: /^tower waterless/i, seat: 'tower', tap: true, hidden: true,
      on: 'Hold the power button for 2–3 seconds, or press ON/OFF on the remote.',
      strong: 'Use the remote to pick its strongest level.', timer: 'Check on the remote that a timer isn\'t set.',
      pulse: 'It sprays in bursts with pauses in between; the level sets the rhythm.', cord: 'Use the cable and adapter that came in the box.' },
    'tower-plus': { name: 'Tower+', h: ['tower-waterless-app-enabled-scent-diffuser-aromar'], kw: /tower\+/i, seat: 'tower', tap: true, hidden: true,
      on: 'Hold the power button for 2–3 seconds, or press ON/OFF on the remote.',
      strong: 'Use the remote or the app to pick its strongest level.', timer: 'Check the remote or app for a timer or schedule.',
      pulse: 'It sprays in bursts with pauses in between; the level sets the rhythm.', cord: 'Use the cable and adapter that came in the box.' },
    humid: { name: 'Humidifying diffuser', humid: true, h: [], seat: 'humid',
      on: 'Press the power button (often the mist button) once.',
      cord: 'Use the adapter that came in the box. Phone fast-chargers can cause odd behavior.' },
  };
  Object.entries(M).forEach(([k, m]) => { m.key = k; });
  const byHandle = {};
  Object.values(M).forEach((m) => m.h.forEach((h) => { byHandle[h] = m; }));
  const manualUrl = (m) => m.manual
    ? `https://cdn.shopify.com/s/files/1/0658/4644/4213/files/aroma-paradise-${m.manual}-waterless-diffuser-manual.pdf` : null;

  /* ------------------------------------------------------------------ helpers */
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const icon = (d) => `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${d}"/></svg>`;
  const I = {
    next: 'M9 6l6 6-6 6', back: 'M15 6l-6 6 6 6', check: 'M5 12.5l4.2 4.2L19 7', x: 'M6 6l12 12M18 6L6 18',
    power: 'M12 3v9M7.5 6.2a7 7 0 1 0 9 0', mist: 'M8 20c0-3 2-3 2-6s-2-3-2-6M12 20c0-3 2-3 2-6s-2-3-2-6M16 20c0-3 2-3 2-6s-2-3-2-6',
    weak: 'M4 15h3M9 12h3M14 9h3M19 6h1', stop: 'M6 12h3l2-4 2 8 2-4h3', noise: 'M4 10v4h4l5 4V6L8 10zM16.5 9a4 4 0 0 1 0 6M19 6.5a8 8 0 0 1 0 11',
    app: 'M7 3h10a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zM11 18h2', strong: 'M12 3c3 4 6 7 6 11a6 6 0 0 1-12 0c0-4 3-7 6-11z',
    video: 'M3 7h12v10H3zM15 10l6-3v10l-6-3', leak: 'M12 3c3 4 6 7 6 11a6 6 0 0 1-12 0c0-4 3-7 6-11zM9 21h6',
    light: 'M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10.5c.7.7 1 1.5 1 2.5h6c0-1 .3-1.8 1-2.5A6 6 0 0 0 12 3z', battery: 'M3 8h15v8H3zM21 11v2M6 11v2M9 11v2',
    remote: 'M8 2h8a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM12 6v.01M10 10h4M10 14h4', timer: 'M12 13V9M9 2h6M12 21a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', drop: 'M12 3c3 4 6 7 6 11a6 6 0 0 1-12 0c0-4 3-7 6-11z',
  };
  const img = (name, alt = '') => {
    const i = CFG.img[name];
    return i ? `<img src="${i.l}" srcset="${i.s} 480w, ${i.l} 900w" sizes="(max-width: 749px) 300px, 420px" width="900" height="900" alt="${esc(alt)}" onerror="(this.closest('figure,span')||this).classList.add('is-missing')">` : '';
  };
  // Never mix formats: humidifying flows only ever show humidifying art.
  const HUMID_ART = ['sym-humid', 'step-disk'];
  // The scene art shows the white fluted Tower Pro, so only Tower Pro gets it; every other
  // waterless model gets its own product photo (owner rule: render the product exactly).
  const artFor = (m, name) => {
    if (!name) return null;
    if (m.humid) return HUMID_ART.includes(name) ? name : null;
    if (m.key !== 'tower-pro') return CFG.products[m.h[0]] ? { product: CFG.products[m.h[0]].i } : null;
    return name;
  };
  // Site-wide tracker (snippets/ap-track.liquid) -> GA4 + dashboard. Flat props only, no PII.
  const track = (event, data = {}) => {
    const p = {}; Object.entries(data || {}).forEach(([k, v]) => { if (Array.isArray(v)) p[k] = v.join(','); else if (v == null || typeof v !== 'object') p[k] = v; });
    if (typeof window.apTrack === 'function') window.apTrack(`diffuser_help_${event}`, p, 'diffuser_help');
  };


  /* ------------------------------------------------------------------ troubleshooting flows */
  // All troubleshooting copy lives in per-family graphs (assets/ap-dh-flows.js, built from
  // docs/research/diffuser-help/flows/*.json). This file only plays them.
  const FLOWS = window.APDH_FLOWS || {};
  const familyOf = {};
  Object.values(FLOWS).forEach((f) => f.models.forEach((k) => { familyOf[k] = f; }));
  const applies = (c, k) => !!c && (!c.only || c.only.includes(k)) && (!c.except || !c.except.includes(k));
  const fill = (m, t) => String(t ?? '').replace(/\{(\w+)\}/g, (_, k) => familyOf[m.key]?.facts?.[m.key]?.[k] ?? '');
  function issuesFor(m) {
    const f = familyOf[m.key];
    return f ? Object.entries(f.issues).filter(([, v]) => !v.models || v.models.includes(m.key)) : [];
  }
  const issueDef = () => familyOf[state.m.key]?.issues?.[state.issue];
  // Follow a node id past nodes that don't apply to this model.
  function resolveNode(id) {
    const nodes = issueDef().nodes;
    for (let guard = 0; guard < 50 && id; guard += 1) {
      const n = nodes[id];
      if (!n) return null;
      if (applies(n, state.m.key) || n.type === 'escalate') return { id, n };
      id = n.type === 'ask' ? n.options[0].next : n.next;
    }
    return null;
  }
  // Steps left on the LONGEST remaining path, so the counter can only finish early, never jump.
  function remaining(id, seen = new Set()) {
    const r = resolveNode(id);
    if (!r || r.n.type === 'escalate' || seen.has(r.id)) return 0;
    const s2 = new Set(seen).add(r.id);
    const nexts = r.n.type === 'ask' ? r.n.options.map((o) => o.next) : [r.n.next];
    return 1 + Math.max(0, ...nexts.filter(Boolean).map((nx) => remaining(nx, s2)));
  }
  const lines = (m, body) => (body || []).filter((b) => typeof b === 'string' || applies(b, m.key))
    .map((b) => fill(m, typeof b === 'string' ? b : b.text)).filter(Boolean);
  const ICON_FOR = { power: 'power', mist: 'mist', weak: 'weak', stop: 'stop', noise: 'noise', app: 'app', strong: 'strong', drop: 'drop', leak: 'leak', light: 'light', battery: 'battery', remote: 'remote' };

  /* ------------------------------------------------------------------ owned diffusers */
  const owned = [];
  (CFG.owned || []).forEach((o) => {
    const add = (m, title, image) => {
      if (!m) return;
      const prev = owned.find((x) => x.m === m);
      if (prev) return; // orders arrive newest first
      owned.push({ m, title, image, order: o.o, date: o.d });
    };
    if (o.b) {
      try { JSON.parse(o.b).forEach((it) => add(matchTitle(it.title), it.title, null)); } catch (e) { /* malformed bundle json */ }
      return;
    }
    add(byHandle[o.h] || matchTitle(o.t, o.w), o.t, o.i);
  });
  function matchTitle(title = '', waterless) {
    if (!/diffuser|tower|plug-in|hvac/i.test(title) || /oil|cleaner|solution/i.test(title)) return null;
    if (waterless === false || (!/waterless|cold air|cold-air/i.test(title) && waterless !== true)) return M.humid;
    return Object.values(M).find((m) => m.kw && m.kw.test(title)) || null;
  }

  /* ------------------------------------------------------------------ state (from the URL) */
  const state = {};
  function readURL() {
    const q = new URLSearchParams(location.search);
    return {
      track: q.get('track'), model: q.get('model'), issue: q.get('issue'),
      path: (q.get('path') || '').split('.').filter(Boolean), s: Number(q.get('s') || 0) || 0,
    };
  }
  function urlFor(p) {
    const q = new URLSearchParams();
    if (p.track) q.set('track', p.track);
    if (p.model) q.set('model', p.model);
    if (p.issue) q.set('issue', p.issue);
    if (p.path && p.path.length) q.set('path', p.path.join('.'));
    if (p.track === 'use' && p.s) q.set('s', String(p.s));
    const qs = q.toString();
    return location.pathname + (qs ? `?${qs}` : '');
  }
  function current() {
    return { track: state.track, model: state.m?.key, issue: state.issue, path: [...state.path], s: state.s };
  }
  function hydrate() {
    const u = readURL();
    Object.assign(state, {
      track: u.track === 'use' || u.track === 'fix' ? u.track : null, m: M[u.model] || null, issue: null,
      node: null, seen: 0, tried: [], answers: [], resolved: false, path: [], s: 0, view: 'welcome',
    });
    if (!state.track && state.m) state.track = 'fix'; // older links: ?model=&issue=
    if (!state.track) { state.view = root.querySelector('[data-apdh-sent]') ? 'sent' : 'welcome'; return; }
    if (!state.m) { state.view = 'pick'; return; }
    if (state.track === 'use') { state.view = 'use'; state.s = Math.min(Math.max(0, u.s), howto(state.m).length); return; }
    const iss = issuesFor(state.m).find(([k]) => k === u.issue);
    if (!iss) { state.view = 'issue'; return; }
    state.issue = u.issue; state.view = 'step';
    const start = resolveNode(iss[1].start);
    state.node = start ? start.id : null; state.seen = 0;
    for (const t of u.path) { if (!applyToken(t)) break; state.path.push(t); }
  }
  // Replays one recorded answer: o<n> = option n, n = "not yet" / "not that", f = "fixed" / "that's it".
  function applyToken(t) {
    const r = resolveNode(state.node);
    if (!r || state.resolved) return false;
    const { n } = r;
    if (t[0] === 'o' && n.type === 'ask') {
      const opt = n.options[Number(t.slice(1))];
      if (!opt) return false;
      state.answers.push(`${fill(state.m, n.title).replace(/\?$/, '')}: ${fill(state.m, opt.label)}`);
      return step(opt.next);
    }
    if (t === 'n' && (n.type === 'step' || n.type === 'info') && n.next) {
      if (n.type === 'step') state.tried.push(fill(state.m, n.title)); else state.answers.push(`Not this: ${fill(state.m, n.title)}`);
      return step(n.next);
    }
    if (t === 'f' && (n.type === 'step' || n.type === 'info')) {
      if (n.type === 'step') state.tried.push(fill(state.m, n.title));
      state.resolved = n.type === 'info' ? 'normal' : 'fixed'; state.view = 'resolved';
      return true;
    }
    return false;
  }
  function step(next) {
    const r = resolveNode(next);
    if (!r) return false;
    state.node = r.id; state.seen += 1;
    return true;
  }

  let pushed = 0;
  function nav(p) {
    try { history.pushState({ apdh: true }, '', urlFor(p)); pushed += 1; } catch (e) { location.href = urlFor(p); return; }
    hydrate(); render(true);
  }
  function parent() {
    const p = current();
    if (p.track === 'use' && p.model && p.s > 0) return { ...p, s: p.s - 1 };
    if (p.path.length) return { ...p, path: p.path.slice(0, -1) };
    if (p.issue) return { track: p.track, model: p.model };
    if (p.model) return { track: p.track };
    return {};
  }
  function goBack() {
    if (pushed > 0) { history.back(); return; }
    history.replaceState({ apdh: true }, '', urlFor(parent()));
    hydrate(); render(true);
  }
  window.addEventListener('popstate', () => { if (pushed > 0) pushed -= 1; stopTimer(); hydrate(); render(true); });

  /* ------------------------------------------------------------------ how-to-use data */
  // Walkthrough steps come from assets/ap-dh-howto.js (built from the manuals audit).
  // Until a model has audited steps, fall back to the manual-backed fields on M.
  function howto(m) {
    const audited = HOWTO[m.key];
    if (audited && audited.steps && audited.steps.length) return audited.steps;
    if (m.humid) {
      return [
        { title: 'Fill it to the line', body: ['Fill the tank with water up to the max line, never past it.', 'Add oil made for humidifying diffusers.'] },
        { title: 'Put the cover on properly', body: ['Seat the cover so it sits evenly. Many models won\'t mist with the cover loose.'] },
        { title: 'Switch it on', body: [m.on, m.cord] },
        { title: 'Keep the air inlet clear', body: ['Stand it on a hard, flat surface and keep the vent where it pulls in air uncovered.'] },
        { title: 'Keep it clean', body: ['When the mist gets weaker, unplug it, empty it and wipe the small ultrasonic disc with a cotton swab and 70–91% isopropyl rubbing alcohol.'] },
      ];
    }
    return [
      { title: 'Add your oil', body: ['Use waterless fragrance oil only.', 'Keep the diffuser upright whenever there\'s oil inside.'] },
      { title: 'Power it up', body: [m.cord, m.on].filter(Boolean) },
      { title: 'Choose how strong', body: [m.strong, m.pulse].filter(Boolean) },
      { title: 'Know the timer', body: [m.timer].filter(Boolean) },
      { title: 'Keep it clean', body: ['Run a cleaning flush every 1–2 months and whenever you switch scents.'] },
    ].filter((st) => st.body.length);
  }
  function useArt(m, title) {
    if (m.humid) return /clean|disc/i.test(title) ? 'step-disk' : 'sym-humid';
    if (m.key !== 'tower-pro') return CFG.products[m.h[0]] ? { product: CFG.products[m.h[0]].i } : null;
    if (/oil|fill|bottle|install|refill/i.test(title)) return 'step-seat';
    if (/clean|care|flush/i.test(title)) return 'step-flush';
    if (/place|where|safety|room/i.test(title)) return 'step-place';
    if (/strong|intensity|level|timer/i.test(title)) return 'sym-weak';
    return 'care';
  }

  /* ------------------------------------------------------------------ views */
  const mediaHtml = (art, m) => !art ? '' : art.product
    ? `<figure class="apdh-step__media apdh-step__media--product"><img src="${esc(art.product)}" alt="${esc(m.name)}" width="600" height="600"></figure>`
    : `<figure class="apdh-step__media">${img(art)}</figure>`;
  // One slim bar per screen (owner 2026-10-04: compact rail, less scroll): back, where you are, step count.
  function ctxBar({ n, total } = {}) {
    const m = state.m;
    const crumbs = [state.track === 'use' ? 'How to use' : 'Fix a problem'];
    if (m) crumbs.push(m.name);
    if (state.issue && state.track === 'fix') crumbs.push(issueDef().label);
    const thumb = m && CFG.products[m.h[0]] ? `<img src="${esc(CFG.products[m.h[0]].i)}" alt="" width="32" height="32">` : '';
    return `<div class="apdh-bar">
        ${thumb}<p class="apdh-bar__ctx">${crumbs.map((c, i) => `<span${i === crumbs.length - 1 ? ' class="is-now"' : ''}>${esc(c)}</span>`).join('')}</p>
        ${total ? `<span class="apdh-bar__count">${n}/${total}</span>` : ''}
      </div>${total ? `<div class="apdh-meter" aria-hidden="true"><span style="width:${(n / total) * 100}%"></span></div>` : ''}`;
  }

  function viewWelcome() {
    const first = CFG.customer?.first;
    const mine = owned.length ? `<p class="apdh-welcome__mine">${icon(I.check)}${first ? `Hi ${esc(first)}. ` : ''}We found ${esc(owned.slice(0, 3).map((o) => (o.m.humid ? 'your humidifying diffuser' : `your ${o.m.name}`)).join(', '))} on your orders.</p>` : '';
    return `
      <div class="apdh-welcome">
        <figure class="apdh-welcome__art" aria-hidden="true">
          <picture><source media="(max-width: 749px)" srcset="${esc(CFG.hero.m)}" width="800" height="420"><img src="${esc(CFG.hero.sq)}" width="900" height="900" alt="" fetchpriority="high"></picture>
        </figure>
        <div class="apdh-welcome__copy">
          <p class="apdh-eyebrow apdh-eyebrow--dark">Diffuser help desk</p>
          <h1 class="apdh-h1" tabindex="-1" data-focus>How can we <em>help today?</em></h1>
          ${mine}
          <div class="apdh-tracks">
            <button type="button" class="apdh-track" data-track="fix" style="--d:0">
              <span class="apdh-track__ico">${icon(I.mist)}</span>
              <span class="apdh-track__txt"><strong>Something's not right</strong><small>Low scent, won't turn on, stops, noise or app trouble. We'll walk you through the fix.</small></span>${icon(I.next)}
            </button>
            <button type="button" class="apdh-track" data-track="use" style="--d:1">
              <span class="apdh-track__ico">${icon(I.power)}</span>
              <span class="apdh-track__txt"><strong>Show me how to use it</strong><small>New to your diffuser? Setup, power, strength, timer and lights, one step at a time.</small></span>${icon(I.next)}
            </button>
          </div>
          <nav class="apdh-guides" aria-label="Guides">
            <a href="${L.care}">${icon(I.drop)}Care &amp; cleaning guide</a>
            <a href="${L.tips}">${icon(I.weak)}Get more scent</a>
            <a href="${L.manuals}">${icon(I.app)}Manuals</a>
          </nav>
        </div>
      </div>`;
  }

  function viewPick() {
    const fix = state.track === 'fix';
    const ownedHtml = owned.length ? `
      <div class="apdh-mine">
        <p class="apdh-step__kicker">On your orders</p>
        <div class="apdh-mine__list">${owned.map((o, idx) => `
          <button type="button" class="apdh-owned" data-pick="${o.m.key}" style="--d:${idx}">
            <span class="apdh-owned__img">${o.image || CFG.products[o.m.h[0]]?.i ? `<img src="${esc(o.image || CFG.products[o.m.h[0]].i)}" alt="" width="120" height="120" loading="lazy">` : icon(I.drop)}</span>
            <span class="apdh-owned__txt"><strong>${esc(o.m.humid ? shortTitle(o.title) : o.m.name)}</strong><small>Order ${esc(o.order)} · ${esc(o.date)}</small></span>
            ${icon(I.next)}
          </button>`).join('')}
        </div>
      </div>` : '';
    const tiles = Object.values(M).filter((m) => !m.hidden && !m.humid).map((m, idx) => {
      const p = CFG.products[m.h[0]];
      return `<button type="button" class="apdh-model" data-pick="${m.key}" style="--d:${idx}">
        <span class="apdh-model__img">${p ? `<img src="${esc(p.i)}" alt="" width="150" height="150" loading="lazy">` : ''}</span>
        <span class="apdh-model__name">${esc(m.name).replace('Plug-In', 'Plug‑In')}</span></button>`;
    }).join('');
    return `
      ${ctxBar()}
      <h2 class="apdh-h2" tabindex="-1" data-focus>${fix ? 'Which diffuser needs help?' : 'Which diffuser do you have?'}</h2>
      ${ownedHtml}
      <div class="apdh-seg" role="radiogroup" aria-label="Diffuser type">
        <button type="button" role="radio" aria-checked="true" data-type="w">Waterless <small>Uses oil, no water</small></button>
        <button type="button" role="radio" aria-checked="false" data-type="h">Humidifying <small>Uses water + oil</small></button>
      </div>
      <div class="apdh-models" data-models="w">${tiles}</div>
      <div class="apdh-humidpick" data-models="h" hidden>
        <figure class="apdh-humidpick__img">${img('sym-humid', 'A humidifying diffuser on a bathroom counter')}</figure>
        <div>
          <p class="apdh-lead">Our humidifying (ultrasonic) diffusers share the same parts: a water tank, a cover and a small ultrasonic disc that turns water into mist. One guide covers them all.</p>
          <button type="button" class="apdh-btn apdh-btn--wine" data-pick="humid">${fix ? 'Help with my humidifying diffuser' : 'Show me how to use it'} ${icon(I.next)}</button>
        </div>
      </div>
      <p class="apdh-hint">Not sure which model? The model number is on the label underneath, and our <a href="${L.manuals}">manuals page</a> shows where to look.${CFG.customer ? '' : ` Or <a href="${L.login}">sign in</a> and we'll find the diffusers you've ordered.`}</p>`;
  }
  function shortTitle(t) { return String(t || 'Humidifying diffuser').split(/[|(]/)[0].trim().slice(0, 60); }

  function viewIssue() {
    const m = state.m;
    const list = issuesFor(m);
    return `
      ${ctxBar()}
      <h2 class="apdh-h2" tabindex="-1" data-focus>What's it doing?</h2>
      <div class="apdh-issues is-plain">${list.map(([k, v], idx) => `
        <button type="button" class="apdh-issue apdh-issue--plain" data-issue="${k}" style="--d:${idx}">
          <span class="apdh-issue__txt"><span class="apdh-issue__ico">${icon(I[ICON_FOR[v.icon]] || I.mist)}</span><strong>${esc(v.label)}</strong><small>${esc(v.sub)}</small></span>
        </button>`).join('')}
      </div>
      ${list.length ? '' : `<p class="apdh-lead">We're still writing the guide for this one. <a href="/pages/contact">Contact our team</a> and we'll help directly.</p>`}
      <p class="apdh-hint">No problem, just learning? <button type="button" class="apdh-inline" data-switch="use">See how to use your ${esc(m.humid ? 'diffuser' : m.name)}</button>.</p>`;
  }

  function viewStep() {
    const r = resolveNode(state.node);
    if (!r) return viewIssue();
    const { n } = r;
    const m = state.m;
    if (n.type === 'escalate') return viewEscalate(n);
    const at = state.seen + 1;
    const total = state.seen + remaining(r.id);
    const media = n.img ? mediaHtml(artFor(m, n.img), m) : '';
    const head = ctxBar({ n: at, total });
    if (n.type === 'ask') {
      return `${head}<article class="apdh-step${media ? '' : ' apdh-step--text'}">${media}<div class="apdh-step__body">
        <p class="apdh-step__kicker">Quick check</p><h2 class="apdh-h2" tabindex="-1" data-focus>${esc(fill(m, n.title))}</h2>
        ${n.lead ? `<p class="apdh-lead">${esc(fill(m, n.lead))}</p>` : ''}
        <div class="apdh-opts">${n.options.map((o, idx) => `<button type="button" class="apdh-opt" data-opt="${idx}">${esc(fill(m, o.label))}${icon(I.next)}</button>`).join('')}</div>
      </div></article>`;
    }
    const info = n.type === 'info';
    const nextR = n.next ? resolveNode(n.next) : null;
    const strong = state.issue === 'strong';
    const yes = info ? n.yes : strong ? 'Better' : 'It\'s working';
    const no = info ? n.no : nextR?.n.type === 'escalate' ? 'Not yet, get help' : 'Not yet';
    return `${head}<article class="apdh-step${media ? '' : ' apdh-step--text'}">${media}<div class="apdh-step__body">
      <p class="apdh-step__kicker">${esc(n.kicker || (info ? 'Good to know' : 'Try this'))}</p><h2 class="apdh-h2" tabindex="-1" data-focus>${esc(fill(m, n.title))}</h2>
      <ol class="apdh-list${info ? ' apdh-list--plain' : ''}">${lines(m, n.body).map((b) => `<li>${esc(b)}</li>`).join('')}</ol>
      ${n.why ? `<details class="apdh-why"><summary>Why this works</summary><p>${esc(fill(m, n.why))}</p></details>` : ''}
      ${n.timer ? timerHtml(n.timer) : ''}
      <div class="apdh-verdict">
        <p>${info ? 'Does that explain it?' : strong ? 'Better?' : 'Did that fix it?'}</p>
        <button type="button" class="apdh-btn apdh-btn--wine" data-fixed>${icon(I.check)}${esc(yes)}</button>
        ${nextR ? `<button type="button" class="apdh-btn apdh-btn--ghost-dark" data-notfixed>${esc(no)} ${icon(I.next)}</button>` : ''}
      </div>
      ${nextR ? '' : `<p class="apdh-hint">Still not right? <a href="/pages/contact">Tell our team</a> and we'll help directly.</p>`}
    </div></article>`;
  }

  function timerHtml(secs) {
    const mins = Math.round(secs / 60);
    return `<div class="apdh-timer" data-timer="${secs}">
      <button type="button" class="apdh-timer__btn" data-timer-start>${icon(I.timer)}<span>Start a ${mins}-minute timer</span></button>
      <div class="apdh-timer__run" hidden><svg class="apdh-timer__ring" viewBox="0 0 44 44" aria-hidden="true"><circle cx="22" cy="22" r="19"/><circle class="apdh-timer__arc" cx="22" cy="22" r="19"/></svg>
        <span class="apdh-timer__left" role="timer" aria-live="off">${mins}:00</span><button type="button" class="apdh-textbtn" data-timer-stop>Cancel</button></div>
    </div>`;
  }

  function viewEscalate(n) {
    return `
      ${ctxBar()}
      <article class="apdh-esc">
        <span class="apdh-esc__ico">${icon(I.video)}</span>
        <h2 class="apdh-h2" tabindex="-1" data-focus>${state.tried.length ? 'Thanks for trying those.' : 'Thanks for checking.'} <em>Let's get a person on it.</em></h2>
        <p class="apdh-lead">Send your help ticket below. When we reply, send us ${esc(fill(state.m, n.video))}. That's usually all we need to find the fix.</p>
        <div data-apdh-formslot></div>
      </article>`;
  }

  function doneCards(m, { troubleshoot } = {}) {
    const cleanerOwned = m.humid ? CFG.has.cleanerH : CFG.has.cleanerW;
    return `<div class="apdh-done__cards">
      <a class="apdh-done__card" href="${L.care}?type=${m.humid ? 'h' : 'w'}">${icon(I.drop)}<span><strong>Care &amp; cleaning guide</strong><small>Four quick habits</small></span>${icon(I.next)}</a>
      ${m.humid ? '' : `<a class="apdh-done__card" href="${L.tips}">${icon(I.weak)}<span><strong>Get more scent</strong><small>Placement, strength and timing</small></span>${icon(I.next)}</a>`}
      ${troubleshoot ? `<button type="button" class="apdh-done__card" data-switch="fix">${icon(I.mist)}<span><strong>Something not right?</strong><small>Troubleshoot your ${esc(m.humid ? 'diffuser' : m.name)}</small></span>${icon(I.next)}</button>`
        : (cleanerOwned ? '' : `<a class="apdh-done__card" href="${m.humid ? L.cleanerH : L.cleanerW}">${icon(I.mist)}<span><strong>${m.humid ? 'Humidifying' : 'Waterless'} Diffuser Cleaner</strong><small>Made for ${m.humid ? 'ultrasonic' : 'cold-air'} diffusers</small></span>${icon(I.next)}</a>`)}
    </div>`;
  }

  function viewResolved() {
    const m = state.m;
    return `
      <article class="apdh-done">
        <span class="apdh-done__seal" aria-hidden="true">${icon(I.check)}</span>
        <h2 class="apdh-h2" tabindex="-1" data-focus>${state.resolved === 'normal' ? 'Good news: <em>that\'s normal.</em>' : state.issue === 'strong' ? 'Just right. <em>Nice work.</em>' : 'You\'re scenting again. <em>Nice work.</em>'}</h2>
        <p class="apdh-lead">To keep it that way, ${m.humid ? 'keep the water at the line and clean the ultrasonic disc whenever the mist gets weaker.' : 'run a quick cleaning flush every 1–2 months and whenever you switch scents.'}</p>
        ${doneCards(m)}
        <button type="button" class="apdh-textbtn" data-restart>Help with something else</button>
      </article>`;
  }

  function viewUse() {
    const m = state.m;
    const steps = howto(m);
    if (state.s >= steps.length) {
      return `
        ${ctxBar()}
        <article class="apdh-done">
          <span class="apdh-done__seal" aria-hidden="true">${icon(I.check)}</span>
          <h2 class="apdh-h2" tabindex="-1" data-focus>You're all set. <em>Enjoy the scent.</em></h2>
          <p class="apdh-lead">Keep it scenting like day one with a little care, and try a few tricks for a stronger scent.</p>
          ${doneCards(m, { troubleshoot: true })}
          ${manualUrl(m) ? `<a class="apdh-textbtn" href="${manualUrl(m)}" target="_blank" rel="noopener">Download the ${esc(m.name)} manual (PDF)</a>` : ''}
        </article>`;
    }
    const st = steps[state.s];
    const total = steps.length;
    return `
      ${ctxBar({ n: state.s + 1, total })}
      <article class="apdh-step">${mediaHtml(useArt(m, st.title), m)}<div class="apdh-step__body">
        <p class="apdh-step__kicker">${esc(m.humid ? 'Humidifying diffuser' : m.name)}</p><h2 class="apdh-h2" tabindex="-1" data-focus>${esc(st.title)}</h2>
        <ol class="apdh-list">${(st.body || []).filter(Boolean).map((b) => `<li>${esc(b)}</li>`).join('')}</ol>
        <div class="apdh-verdict apdh-verdict--use">
          <button type="button" class="apdh-btn apdh-btn--wine" data-use-next>${state.s + 1 < total ? 'Next' : 'Finish'} ${icon(I.next)}</button>
        </div>
      </div></article>`;
  }

  function viewSent() {
    return `
      <article class="apdh-done">
        <span class="apdh-done__seal" aria-hidden="true">${icon(I.check)}</span>
        <h2 class="apdh-h2" tabindex="-1" data-focus>Your help ticket is <em>with our team.</em></h2>
        <p class="apdh-lead">Watch your inbox. When we reply, send the short video from your phone and we'll take it from there.</p>
        <button type="button" class="apdh-textbtn" data-home>Back to the help desk</button>
      </article>`;
  }

  /* ------------------------------------------------------------------ ticket */
  function renderTicket() {
    const show = state.track === 'fix' && !!state.m;
    ticketEl.hidden = !show;
    if (!show) return;
    const rows = [['Diffuser', state.m.name]];
    if (state.issue) rows.push(['Issue', issueDef().label]);
    state.answers.forEach((a) => rows.push(['Answer', a]));
    const tried = state.tried;
    root.querySelector('[data-apdh-ticket-rows]').innerHTML = rows.map(([k, v]) => `<div><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`).join('')
      + (tried.length ? `<div class="apdh-ticket__tried"><dt>Tried</dt><dd><ul>${tried.map((t) => `<li>${icon(I.check)}${esc(t)}</li>`).join('')}</ul></dd></div>` : '');
    const count = tried.length ? `${tried.length} step${tried.length > 1 ? 's' : ''} tried` : '';
    root.querySelector('[data-apdh-ticket-count]').textContent = count;
    root.querySelector('[data-apdh-stamp]').hidden = !state.resolved;
  }

  function ticketText() {
    const out = [`Diffuser: ${state.m.name}`, `Issue: ${issueDef().label}`];
    state.answers.forEach((a) => out.push(`Answer: ${a}`));
    if (state.tried.length) out.push('', 'Steps I already tried:', ...state.tried.map((t) => `- ${t}`));
    out.push('', 'Anything else you noticed:', '');
    return out.join('\n');
  }

  function mountForm() {
    const slot = panel.querySelector('[data-apdh-formslot]');
    if (!slot) return;
    slot.appendChild(root.querySelector('[data-apdh-form-tpl]').content.cloneNode(true));
    const f = (k) => slot.querySelector(`[data-apdh-f="${k}"]`);
    const c = CFG.customer || {};
    const hit = owned.find((o) => o.m === state.m);
    f('model').value = state.m.name;
    f('issue').value = issueDef().label;
    if (c.name) f('name').value = c.name;
    if (c.email) f('email').value = c.email;
    if (hit) f('order').value = hit.order;
    f('body').value = ticketText();
    slot.querySelector('form').addEventListener('submit', () => track('escalate_submit', { model: state.m.key, issue: state.issue, steps: state.tried.length }));
  }

  /* ------------------------------------------------------------------ render */
  function render(focus) {
    const v = state.view;
    root.dataset.view = v;
    // A render bug must never leave the customer on a frozen screen: fall back to a clear way out.
    try {
      panel.innerHTML = v === 'welcome' ? viewWelcome() : v === 'pick' ? viewPick() : v === 'issue' ? viewIssue()
        : v === 'resolved' ? viewResolved() : v === 'use' ? viewUse() : v === 'sent' ? viewSent() : viewStep();
    } catch (e) {
      track('render_error', { view: v, message: String(e && e.message) });
      panel.innerHTML = `<article class="apdh-done"><h2 class="apdh-h2" tabindex="-1" data-focus>Sorry, that screen didn't load.</h2>
        <p class="apdh-lead">Start again, or <a href="/pages/contact">contact our team</a> and we'll help directly.</p>
        <button type="button" class="apdh-btn apdh-btn--wine" data-home>Start again</button></article>`;
    }
    if (!reduceMotion) { panel.classList.remove('is-in'); void panel.offsetWidth; panel.classList.add('is-in'); }
    if (v === 'step' && resolveNode(state.node)?.n.type === 'escalate') mountForm();
    renderTicket();
    // Header back / start-over icons (snippets/ap-immersive-header): hidden on the first screen.
    window.APIM?.sync?.({ back: v !== 'welcome', restart: v !== 'welcome' });
    root.classList.add('is-ready');
    document.title = `${titleFor()} | ${CFG.shop}`;
    if (focus) {
      const target = panel.querySelector('[data-focus]');
      if (target) target.focus({ preventScroll: true });
      if (window.scrollY > 0) window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  }
  function titleFor() {
    if (state.view === 'welcome' || !state.track) return 'Diffuser Help';
    if (!state.m) return state.track === 'use' ? 'How to use your diffuser' : 'Diffuser troubleshooting';
    if (state.track === 'use') return `How to use your ${state.m.name}`;
    return state.issue ? `${state.m.name}: ${issueDef().label}` : `${state.m.name} troubleshooting`;
  }

  /* ------------------------------------------------------------------ events */
  let timerId = null;
  panel.addEventListener('click', (e) => {
    const t = e.target.closest('button, a');
    if (!t) return;
    const p = current();
    const href = t.matches('a') ? t.getAttribute('href') || '' : '';
    if (href.includes('/products/')) track('product_click', { model: p.model, issue: p.issue, view: state.view, product_handle: href.split('/products/')[1].split(/[?#/]/)[0] });
    if (t.matches('[data-track]')) { track('select_track', { track: t.dataset.track }); return nav({ track: t.dataset.track }); }
    if (t.matches('[data-pick]')) { track('select_model', { model: t.dataset.pick, track: state.track }); return nav({ track: state.track, model: t.dataset.pick }); }
    if (t.matches('[data-switch]')) return nav({ track: t.dataset.switch, model: p.model });
    if (t.matches('[data-type]')) {
      panel.querySelectorAll('[data-type]').forEach((b) => b.setAttribute('aria-checked', String(b === t)));
      panel.querySelectorAll('[data-models]').forEach((g) => { g.hidden = g.dataset.models !== t.dataset.type; });
      return;
    }
    if (t.matches('[data-issue]')) { track('select_issue', { model: p.model, issue: t.dataset.issue }); return nav({ ...p, issue: t.dataset.issue, path: [] }); }
    if (t.matches('[data-back]')) { stopTimer(); return goBack(); }
    if (t.matches('[data-home]')) return nav({});
    if (t.matches('[data-opt]')) return nav({ ...p, path: [...p.path, `o${t.dataset.opt}`] });
    if (t.matches('[data-notfixed]')) {
      stopTimer();
      track('step_not_fixed', { model: p.model, issue: p.issue, step: state.node });
      return nav({ ...p, path: [...p.path, 'n'] });
    }
    if (t.matches('[data-fixed]')) {
      stopTimer();
      track('resolved', { model: p.model, issue: p.issue, step: state.node, steps: state.tried.length + 1 });
      return nav({ ...p, path: [...p.path, 'f'] });
    }
    if (t.matches('[data-use-next]')) {
      if (state.s + 1 >= howto(state.m).length) track('use_complete', { model: p.model });
      return nav({ ...p, s: state.s + 1 });
    }
    if (t.matches('[data-restart]')) return nav({ track: 'fix', model: p.model });
    if (t.matches('[data-timer-start]')) return startTimer(t.closest('[data-timer]'));
    if (t.matches('[data-timer-stop]')) return stopTimer();
  });

  function startTimer(box) {
    stopTimer();
    const total = Number(box.dataset.timer);
    const end = Date.now() + total * 1000;
    const run = box.querySelector('.apdh-timer__run');
    const arc = box.querySelector('.apdh-timer__arc');
    const left = box.querySelector('.apdh-timer__left');
    box.querySelector('[data-timer-start]').hidden = true;
    run.hidden = false;
    const C = 2 * Math.PI * 19;
    arc.style.strokeDasharray = C;
    const tick = () => {
      const sec = Math.max(0, Math.round((end - Date.now()) / 1000));
      left.textContent = `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
      arc.style.strokeDashoffset = C * (1 - sec / total);
      if (sec <= 0) {
        stopTimer();
        box.innerHTML = `<p class="apdh-timer__done">${icon(I.check)}Time's up. Is the scent coming through?</p>`;
        if (navigator.vibrate) navigator.vibrate(180);
      }
    };
    tick();
    timerId = setInterval(tick, 1000);
  }
  function stopTimer() {
    if (!timerId) return;
    clearInterval(timerId); timerId = null;
    const box = panel.querySelector('[data-timer]');
    if (box && box.querySelector('[data-timer-start]')) { box.querySelector('[data-timer-start]').hidden = false; box.querySelector('.apdh-timer__run').hidden = true; }
  }

  window.APIM = Object.assign(window.APIM || {}, {
    back: () => { stopTimer(); goBack(); },
    restart: () => { stopTimer(); track('restart', { view: state.view }); nav({}); },
  });

  /* ------------------------------------------------------------------ boot */
  hydrate();
  render(false);
  track('view', { owned: owned.length, deep_link: !!state.track, view: state.view });
})();
